import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
export function PrivacyPolicyPage() {
  usePageMeta('Privacy Policy | FindOnlineColleges', 'Learn how FindOnlineColleges.org collects, uses, and protects your personal information.');
  return <main className="bg-white py-10 sm:py-16" data-id="element-2148" data-mp="2rrap9b8chp21">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-800" data-id="element-2149" data-mp="2e178v0w5cpki">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-navy mb-8 transition-colors" data-id="element-2150" data-mp="2isgvdsmefcs6">
          <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-2151" data-mp="2454921orp30q" />
          Back to home
        </Link>

        {/* Page header */}
        <header className="border-b border-gray-200 pb-8 mb-12" data-id="element-2152" data-mp="290jjejnkggse">
          <h1 className="text-4xl sm:text-5xl font-bold text-navy tracking-tight leading-tight" data-id="element-2153" data-mp="1v46158b7kbcv">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500 mt-3" data-id="element-2154" data-mp="1z6asg0e6ovwc">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </header>

        {/* Intro */}
        <div className="space-y-5 leading-relaxed text-base" data-id="element-2155" data-mp="2jnq2t7orqn05">
          <p data-id="element-2156" data-mp="2es13n5gnv84d">
            This privacy notice for FindOnlineColleges.org describes how and why
            we might collect, store, use, and/or share your information when you
            use our services, such as when you:
          </p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-2157" data-mp="1m07lv9kyeel2">
            <li data-id="element-2158" data-mp="20s00fjcxbolq">
              Visit our website, or any website of ours that links to this
              privacy notice
            </li>
            <li data-id="element-2159" data-mp="1vy5avzan0feq">
              Engage with us in other related ways, including any sales,
              marketing, or events
            </li>
          </ul>
          <p data-id="element-2160" data-mp="13ahtet4ny4wb">
            <strong className="text-navy" data-id="element-2161" data-mp="13arkyl5cwk44">Questions or concerns?</strong>{' '}
            Reading this privacy notice will help you understand your privacy
            rights and choices. If you do not agree with our policies and
            practices, please do not use our services.
          </p>
        </div>

        {/* Summary of Key Points */}
        <Section title="Summary of Key Points" data-id="element-2162" data-mp="26r3d4urfvpcl">
          <SummaryItem label="What personal information do we process?" text="When you visit, use, or navigate our services, we may process personal information depending on how you interact with us, the choices you make, and the features you use." data-id="element-2163" data-mp="26ps3iebbbndk" />
          <SummaryItem label="Do we process sensitive personal information?" text="We do not process sensitive personal information." data-id="element-2164" data-mp="1j0h696t68b46" />
          <SummaryItem label="Do we receive information from third parties?" text="We may receive technical and advertising data from third parties, including advertising platforms and analytics providers." data-id="element-2165" data-mp="2ugxpzcbrddwu" />
          <SummaryItem label="How do we process your information?" text="To provide, improve, and administer our services, communicate with you, ensure security and fraud prevention, comply with law, and with your consent where required." data-id="element-2166" data-mp="nr3ybkmc32uz" />
          <SummaryItem label="When and with whom do we share personal information?" text="Only in specific circumstances (e.g., business transfers or service providers)." data-id="element-2167" data-mp="94anosnrel94" />
          <SummaryItem label="How do we keep your information safe?" text="With organizational and technical measures, but no method is 100% secure." data-id="element-2168" data-mp="1t1ufc0numn" />
          <SummaryItem label="What are your rights?" text="Depending on your location, you may have rights under applicable privacy laws." data-id="element-2169" data-mp="3maub4p6607mz" />
          <SummaryItem label="How do you exercise your rights?" text="You can exercise rights by contacting us (see Section 12)." data-id="element-2170" data-mp="1kio79lbhcejc" />
        </Section>

        {/* TOC */}
        <Section title="Table of Contents" data-id="element-2171" data-mp="1xi6v6vla9j27">
          <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-2172" data-mp="1xh5lovjx14ml">
            {tocItems.map(item => <li key={item.id} data-id="element-2173" data-mp="1ikobi7ngpw1m">
                <a href={`#${item.id}`} className="text-navy hover:text-crimson hover:underline transition-colors" data-id="element-2174" data-mp="1nkam09bwoy6q">
                  {item.label}
                </a>
              </li>)}
          </ol>
        </Section>

        {/* Section 1 */}
        <NumberedSection id="section-1" number={1} title="What Information Do We Collect?" data-id="element-2175" data-mp="zuygpkjp9a9p">
          <SubHeading data-id="element-2176" data-mp="14uuire8u6rmm">Personal information you disclose to us</SubHeading>
          <Paragraph data-id="element-2177" data-mp="qti4d79bqy2u">
            We collect personal information that you voluntarily provide to us
            when you express an interest in obtaining information about us or
            our services, when you participate in activities on the services, or
            otherwise when you contact us.
          </Paragraph>
          <Paragraph data-id="element-2178" data-mp="799pu3ajw43g">
            <strong className="text-navy" data-id="element-2179" data-mp="7a2rwqyke5f9">
              Personal Information Provided by You.
            </strong>{' '}
            The personal information we collect may include:
          </Paragraph>
          <BulletList items={['Names', 'Phone numbers', 'Email addresses', 'Mailing addresses', 'Education level and interests', 'Program preferences']} data-id="element-2180" data-mp="g7lgu6fbx3wf" />

          <SubHeading data-id="element-2181" data-mp="13x7fmsrshh3w">Information automatically collected</SubHeading>
          <Paragraph data-id="element-2182" data-mp="zwds40i9e4bj">
            We automatically collect certain information when you visit, use, or
            navigate the services. This information does not reveal your
            specific identity but may include device and usage information, such
            as:
          </Paragraph>
          <BulletList items={['IP address', 'Browser and device characteristics', 'Operating system', 'Language preferences', 'Referring URLs', 'Device name and type', 'Pages viewed and time spent', 'Clickstream data']} data-id="element-2183" data-mp="6kph1bxwmdal" />
        </NumberedSection>

        {/* Section 2 */}
        <NumberedSection id="section-2" number={2} title="How Do We Process Your Information?" data-id="element-2184" data-mp="p9m16mzl7pf3">
          <Paragraph data-id="element-2185" data-mp="ubuwhpzxn3kd">
            We process your personal information for a variety of reasons,
            including:
          </Paragraph>
          <BulletList items={['To facilitate account creation and authentication', 'To deliver and facilitate delivery of services to you', 'To respond to user inquiries and offer support', 'To send administrative information to you', 'To fulfill and manage your requests', 'To enable user-to-user communications', 'To request feedback and contact you about your use of our services', 'To send you marketing and promotional communications', 'To deliver targeted advertising to you', 'To protect our services and investigate potential violations', 'To identify usage trends and determine the effectiveness of campaigns', "To save or protect an individual's vital interest", 'To comply with our legal obligations']} data-id="element-2186" data-mp="13aydmofjh8w8" />

          <SubHeading data-id="element-2187" data-mp="36hdvha2u3tpv">Email Communications</SubHeading>
          <Paragraph data-id="element-2188" data-mp="32hn7fe4ozrp1">
            When you submit a form on our website, you may consent to receive
            marketing and promotional emails from FindOnlineColleges.org and its
            educational partners at the email address you provide. Marketing
            emails are sent from{' '}
            <span className="font-medium text-navy" data-id="element-2189" data-mp="3q783zngd09op">
              contact@findonlinecolleges.org
            </span>
            . Every marketing email includes an unsubscribe link. You may opt
            out at any time and your request will be honored within 10 business
            days. Opting out of marketing email does not affect transactional or
            administrative messages related to a specific inquiry you have
            submitted.
          </Paragraph>
        </NumberedSection>

        {/* Section 3 */}
        <NumberedSection id="section-3" number={3} title="SMS / Text Message Communications" data-id="element-2190" data-mp="hq3m1fg222jp">
          <SubHeading data-id="element-2191" data-mp="3zi1znev7mk8">How We Collect SMS Consent</SubHeading>
          <Paragraph data-id="element-2192" data-mp="8xt8pp9v5gy8">
            When you submit a form on our website, you may expressly consent to
            receive autodialed and/or pre-recorded marketing text messages
            (SMS/MMS) from FindOnlineColleges.org and its educational partners at
            the mobile phone number you provide. This consent is obtained at the
            point of form submission and is documented with the date, time, IP
            address, and page URL at which consent was given.
          </Paragraph>

          <SubHeading data-id="element-2193" data-mp="1kiplgs8ur2lr">How We Use Your Phone Number for SMS</SubHeading>
          <Paragraph data-id="element-2194" data-mp="1fkxqtqgtp2dv">
            If you have provided consent, we may send you text messages for
            purposes including:
          </Paragraph>
          <BulletList items={['Information about educational programs matching your interests', 'Follow-up communications related to your inquiry', 'Promotional offers and marketing messages from us or our educational partners']} data-id="element-2195" data-mp="rvda7e856h7r" />

          <SubHeading data-id="element-2196" data-mp="ww9e3rshu3d6">Consent Is Not Required</SubHeading>
          <Paragraph data-id="element-2197" data-mp="2d7dbt28gki66">
            Your consent to receive SMS/MMS messages is never a condition of
            using our services or receiving information. You may choose not to
            provide consent and still access our website and resources.
          </Paragraph>

          <SubHeading data-id="element-2198" data-mp="1bklxfsx09wt6">Message Frequency and Rates</SubHeading>
          <Paragraph data-id="element-2199" data-mp="1bmf4387mch16">
            Message frequency varies based on your interactions and preferences.
            Standard message and data rates may apply depending on your carrier
            and plan.
          </Paragraph>

          <SubHeading data-id="element-2200" data-mp="xsxh1dfq4m6e">How to Opt Out</SubHeading>
          <Paragraph data-id="element-2201" data-mp="xtp8cc2afxqv">
            You may revoke your consent and opt out of SMS communications at any
            time by:
          </Paragraph>
          <BulletList items={['Replying STOP to any text message you receive from us', 'Contacting us directly using the information in Section 13']} data-id="element-2202" data-mp="nurm5fsc4gb1" />
          <Paragraph data-id="element-2203" data-mp="2i9al58do41ob">
            Opt-out requests will be honored promptly. After opting out, you may
            receive a single confirmation message. You will not receive further
            SMS marketing messages unless you provide new consent.
          </Paragraph>

          <SubHeading data-id="element-2204" data-mp="2lovl0ddzeuph">For Assistance</SubHeading>
          <Paragraph data-id="element-2205" data-mp="2pr8jqhub9scr">
            Reply HELP to any text message for support information, or contact
            us directly using the information in Section 13.
          </Paragraph>

          <SubHeading data-id="element-2206" data-mp="2zicwrc0ff34y">Third-Party SMS Senders</SubHeading>
          <Paragraph data-id="element-2207" data-mp="2zjxrb1mqqm2t">
            When you consent to receive communications from our educational
            partners, those partners may contact you directly via SMS under
            their own messaging programs and privacy policies. We encourage you
            to review the privacy policies of any educational institution or
            partner that contacts you.
          </Paragraph>

          <SubHeading data-id="element-2208" data-mp="2gpiqd6gvcjcc">Record Retention</SubHeading>
          <Paragraph data-id="element-2209" data-mp="3n8h15llfv6x0">
            We retain records of SMS consent, including timestamp, IP address,
            form URL, and submitted form data, in accordance with our general
            data retention practices described in Section 6.
          </Paragraph>
        </NumberedSection>

        {/* Section 4 */}
        <NumberedSection id="section-4" number={4} title="When and With Whom Do We Share Your Personal Information?" data-id="element-2210" data-mp="2xiqpiu1c7zj9">
          <Paragraph data-id="element-2211" data-mp="37hzet9l9cpza">
            We may share information in the following situations:
          </Paragraph>
          <ul className="space-y-3 mt-4" data-id="element-2212" data-mp="2jrlxoe47objh">
            {[{
            label: 'Business Transfers',
            text: 'In connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition.'
          }, {
            label: 'Educational Institutions',
            text: 'We share your information with schools and educational partners that match your interests.'
          }, {
            label: 'Service Providers',
            text: 'With third-party vendors, service providers, contractors, or agents who perform services for us.'
          }, {
            label: 'Advertising Partners',
            text: 'With advertising partners to deliver targeted ads.'
          }, {
            label: 'Legal Requirements',
            text: 'When required by law or to protect rights, safety, and property.'
          }].map(item => <li key={item.label} className="flex gap-3 leading-relaxed text-gray-700" data-id="element-2213" data-mp="ucb6w667xaok">
                <span className="text-navy mt-2 w-1.5 h-1.5 rounded-full bg-navy flex-shrink-0" data-id="element-2214" data-mp="z806uam5gtle" />
                <span data-id="element-2215" data-mp="lcefvxwcvzqc">
                  <strong className="text-navy" data-id="element-2216" data-mp="pbn5cqkd5bg3">{item.label}:</strong>{' '}
                  {item.text}
                </span>
              </li>)}
          </ul>
        </NumberedSection>

        {/* Section 5 */}
        <NumberedSection id="section-5" number={5} title="Do We Use Cookies and Other Tracking Technologies?" data-id="element-2217" data-mp="3a2e4zzrvwy3p">
          <Paragraph data-id="element-2218" data-mp="3qb3l93gagdhn">
            Yes, we use cookies and similar tracking technologies to access or
            store information. See our Cookie Notice for more details about how
            we use cookies.
          </Paragraph>
        </NumberedSection>

        {/* Section 6 */}
        <NumberedSection id="section-6" number={6} title="How Long Do We Keep Your Information?" data-id="element-2219" data-mp="3bm7dl7i592hk">
          <Paragraph data-id="element-2220" data-mp="83xd9y42mdv3">
            We keep your information for as long as necessary to fulfill the
            purposes outlined in this privacy notice unless otherwise required
            by law. When we have no ongoing legitimate business need to process
            your personal information, we will delete or anonymize it.
          </Paragraph>
        </NumberedSection>

        {/* Section 7 */}
        <NumberedSection id="section-7" number={7} title="How Do We Keep Your Information Safe?" data-id="element-2221" data-mp="2jtgix1g3zluf">
          <Paragraph data-id="element-2222" data-mp="zcgy2t5aibcg">
            We have implemented appropriate technical and organizational
            security measures designed to protect the security of any personal
            information we process. However, no electronic transmission over the
            internet or information storage technology can be guaranteed to be
            100% secure.
          </Paragraph>
        </NumberedSection>

        {/* Section 8 */}
        <NumberedSection id="section-8" number={8} title="Do We Collect Information From Minors?" data-id="element-2223" data-mp="1i40293b1vvg2">
          <Paragraph data-id="element-2224" data-mp="1r23o60py6u8e">
            We do not knowingly solicit data from or market to children under 18
            years of age. If you become aware that a child has provided us with
            personal information, please contact us. If we learn we have
            collected personal information from a child under 18, we will delete
            that information.
          </Paragraph>
        </NumberedSection>

        {/* Section 9 */}
        <NumberedSection id="section-9" number={9} title="What Are Your Privacy Rights?" data-id="element-2225" data-mp="14yl3u1mez3ml">
          <Paragraph data-id="element-2226" data-mp="1dyzrh7muout9">
            Depending on your location, you may have the following rights:
          </Paragraph>
          <BulletList items={['The right to access your personal information', 'The right to request correction of your personal information', 'The right to request deletion of your personal information', 'The right to opt out of marketing communications', 'The right to opt out of the sale or sharing of personal information (where applicable)']} data-id="element-2227" data-mp="18zep0n8h24bs" />
          <Paragraph data-id="element-2228" data-mp="1sq08i11nxx10">
            To exercise these rights, please contact us using the information in
            Section 13.
          </Paragraph>

          <SubHeading data-id="element-2229" data-mp="21pd8m33wd992">Opt Out of Marketing Email</SubHeading>
          <Paragraph data-id="element-2230" data-mp="21mr2m7wnn1sy">
            To opt out of marketing email: Click the unsubscribe link in any
            marketing email you receive from us, or contact us at the address in
            Section 13. Requests are processed within 10 business days.
          </Paragraph>

          <SubHeading data-id="element-2231" data-mp="2bbxzf13hfvzs">
            California Residents — Opt Out of Sale/Sharing
          </SubHeading>
          <Paragraph data-id="element-2232" data-mp="2g9g1q8mzvcyt">
            California residents — opt out of sale/sharing of personal
            information: To exercise your right to opt out under CCPA/CPRA,
            contact us at the address in Section 13 with the subject line{' '}
            <strong className="text-navy" data-id="element-2233" data-mp="1sm84mdzpza7v">"California Privacy Opt-Out."</strong>{' '}
            We will process your request within 15 business days.
          </Paragraph>
        </NumberedSection>

        {/* Section 10 */}
        <NumberedSection id="section-10" number={10} title="Your California Privacy Rights (CCPA/CPRA)" data-id="element-2234" data-mp="1jmbcg2e4ft82">
          <Paragraph data-id="element-2235" data-mp="1nog6v3ce8tvr">
            This section applies only to California residents. Under the
            California Consumer Privacy Act (CCPA), as amended by the California
            Privacy Rights Act (CPRA), you have specific rights regarding your
            personal information.
          </Paragraph>

          <SubHeading data-id="element-2236" data-mp="1p53wzeoj4umv">Categories of Personal Information Collected</SubHeading>
          <Paragraph data-id="element-2237" data-mp="1p77yjpyyhhjz">
            In the preceding 12 months, we have collected the following
            categories of personal information:
          </Paragraph>
          <BulletList items={['Identifiers (e.g., name, email address, phone number, IP address)', 'Personal information categories listed in the California Customer Records statute (e.g., contact information)', 'Internet or other similar network activity (e.g., browsing history, interaction with our website)', 'Education and program-interest information (e.g., degree level, area of study)', 'Inferences drawn from other personal information (e.g., preferences and characteristics)']} data-id="element-2238" data-mp="1gbs00vn9ae82" />

          <SubHeading data-id="element-2239" data-mp="3d1be31mqhfu8">How We Use and Share Your Information</SubHeading>
          <Paragraph data-id="element-2240" data-mp="3i17ih1zcf9ab">
            We use this information to provide our services, match you with
            educational programs, and for marketing purposes. We may share this
            information with the following categories of third parties:
          </Paragraph>
          <BulletList items={['Educational partners (to fulfill your requests for information)', 'Advertising partners (to deliver targeted advertisements)', 'Service providers (to operate our website and services)']} data-id="element-2241" data-mp="2uc4ulce48k7d" />

          <SubHeading data-id="element-2242" data-mp="26mkaura51ix1">Your CCPA/CPRA Rights</SubHeading>
          <BulletList items={['Right to Know/Access: You can request information about the categories and specific pieces of personal information we have collected about you.', 'Right to Delete: You can request that we delete your personal information, subject to certain exceptions.', 'Right to Correct: You can request that we correct inaccurate personal information we hold about you.', 'Right to Opt-Out: You have the right to opt out of the sale or sharing of your personal information.', 'Right to Limit Use: You have the right to limit the use and disclosure of sensitive personal information (note: we do not generally collect sensitive personal information).', 'Right to Non-Discrimination: We will not discriminate against you for exercising any of your CCPA rights.']} data-id="element-2243" data-mp="26m1323e7cawq" />

          <SubHeading data-id="element-2244" data-mp="1fj5tdbmb5l6o">How to Submit a Request</SubHeading>
          <Paragraph data-id="element-2245" data-mp="i2qppssk3we2">
            To exercise your Right to Know, Delete, or Correct, please contact
            us using the information in Section 13. To exercise your Right to
            Opt-Out of the sale or sharing of your personal information, please
            visit our{' '}
            <Link to="/do-not-sell-my-info" className="text-navy underline hover:text-crimson" data-id="element-2246" data-mp="2wa9fqjx6rtpl">
              Do Not Sell My Info
            </Link>{' '}
            page.
          </Paragraph>
          <Paragraph data-id="element-2247" data-mp="2maqzoekecs2t">
            We will confirm receipt of your request within 10 business days and
            aim to respond within 45 days. If we require more time, we will
            inform you of the reason and extension period in writing. You may
            also use an authorized agent to submit a request on your behalf,
            provided you supply written permission and verify your own identity
            directly with us.
          </Paragraph>
        </NumberedSection>

        {/* Section 11 */}
        <NumberedSection id="section-11" number={11} title="Controls for Do-Not-Track Features" data-id="element-2248" data-mp="1ylpy9b9zeh71">
          <Paragraph data-id="element-2249" data-mp="1jzisnzex665e">
            Most web browsers and some mobile operating systems include a
            Do-Not-Track ("DNT") feature or setting you can activate to signal
            your privacy preference. At this stage, no uniform technology
            standard for recognizing and implementing DNT signals has been
            finalized.
          </Paragraph>
        </NumberedSection>

        {/* Section 12 */}
        <NumberedSection id="section-12" number={12} title="Do We Make Updates to This Notice?" data-id="element-2250" data-mp="22piemdfgr54l">
          <Paragraph data-id="element-2251" data-mp="27oljnxvg1bck">
            Yes, we will update this notice as necessary to stay compliant with
            relevant laws. The updated version will be indicated by an updated
            "Last updated" date at the top of this privacy notice.
          </Paragraph>
        </NumberedSection>

        {/* Section 13 */}
        <NumberedSection id="section-13" number={13} title="How Can You Contact Us About This Notice?" data-id="element-2252" data-mp="2lms0004cmsoh">
          <Paragraph data-id="element-2253" data-mp="ezu8m3e8vie8">
            If you have questions or comments about this notice, please contact
            us at:
          </Paragraph>
          <div className="mt-5 p-6 bg-gray-50 border border-gray-200 rounded-xl" data-id="element-2254" data-mp="a19bwdy7bguj">
            <p className="font-bold text-navy text-lg" data-id="element-2255" data-mp="opvzt41w9gxd">Birddog Media</p>
            <p className="text-gray-700 mt-2" data-id="element-2256" data-mp="jr2l58avfjvm">
              1226 W. Northwest Highway, Palatine, IL 60067
            </p>
            <p className="text-gray-700 mt-2" data-id="element-2257" data-mp="3s7xg81lffzz5">
              Email:{' '}
              <a href="mailto:contact@findonlinecolleges.org" className="text-navy underline hover:text-crimson font-medium" data-id="element-2258" data-mp="3n8uk56nfd43b">
                contact@findonlinecolleges.org
              </a>
            </p>
          </div>
        </NumberedSection>
      </div>
    </main>;
}
/* ---------- Sub-components ---------- */
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="mt-14" data-id="element-2259" data-mp="3i4a71h3meojt">
      <h2 className="text-2xl sm:text-3xl font-bold text-navy mb-6 tracking-tight" data-id="element-2260" data-mp="3i524kxgs2wcq">
        {title}
      </h2>
      <div className="space-y-4" data-id="element-2261" data-mp="3n4er1h3qm1es">{children}</div>
    </section>;
}
function NumberedSection({
  id,
  number,
  title,
  children
}: {
  id: string;
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return <section id={id} className="mt-16 pt-10 border-t border-gray-200 scroll-mt-8" data-id="element-2262" data-mp="3w5cvu1m02cyf">
      <div className="flex items-baseline gap-3 mb-6" data-id="element-2263" data-mp="4xi9g0gh8ayg">
        <span className="text-crimson font-bold text-xl" data-id="element-2264" data-mp="9w1tfvbifco6">{number}.</span>
        <h2 className="text-2xl sm:text-3xl font-bold text-navy tracking-tight leading-tight" data-id="element-2265" data-mp="entn4epypyij">
          {title}
        </h2>
      </div>
      <div className="space-y-5 leading-relaxed text-gray-700 text-base" data-id="element-2266" data-mp="io3v1wxbez01">
        {children}
      </div>
    </section>;
}
function SubHeading({
  children
}: {
  children: React.ReactNode;
}) {
  return <h3 className="text-lg font-bold text-navy mt-8 mb-3" data-id="element-2267" data-mp="soe4r7k55oag">{children}</h3>;
}
function Paragraph({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="text-gray-700 leading-relaxed" data-id="element-2268" data-mp="12owzj9rhg3rq">{children}</p>;
}
function BulletList({
  items
}: {
  items: string[];
}) {
  return <ul className="list-disc pl-6 space-y-2 marker:text-crimson" data-id="element-2269" data-mp="ssk5gas0znwg">
      {items.map(item => <li key={item} className="text-gray-700 leading-relaxed" data-id="element-2270" data-mp="os09haj6uqbw">
          {item}
        </li>)}
    </ul>;
}
function SummaryItem({
  label,
  text
}: {
  label: string;
  text: string;
}) {
  return <div className="border-l-2 border-crimson/40 pl-4 py-1" data-id="element-2271" data-mp="1gi66l3rykd73">
      <p className="text-gray-700 leading-relaxed" data-id="element-2272" data-mp="1gkr6voclyl2q">
        <strong className="text-navy block mb-1" data-id="element-2273" data-mp="1bqwhq5e8hdw9">{label}</strong>
        {text}
      </p>
    </div>;
}
const tocItems = [{
  id: 'section-1',
  label: 'What information do we collect?'
}, {
  id: 'section-2',
  label: 'How do we process your information?'
}, {
  id: 'section-3',
  label: 'SMS / text message communications'
}, {
  id: 'section-4',
  label: 'When and with whom do we share your personal information?'
}, {
  id: 'section-5',
  label: 'Do we use cookies and other tracking technologies?'
}, {
  id: 'section-6',
  label: 'How long do we keep your information?'
}, {
  id: 'section-7',
  label: 'How do we keep your information safe?'
}, {
  id: 'section-8',
  label: 'Do we collect information from minors?'
}, {
  id: 'section-9',
  label: 'What are your privacy rights?'
}, {
  id: 'section-10',
  label: 'Your California Privacy Rights (CCPA/CPRA)'
}, {
  id: 'section-11',
  label: 'Controls for do-not-track features'
}, {
  id: 'section-12',
  label: 'Do we make updates to this notice?'
}, {
  id: 'section-13',
  label: 'How can you contact us about this notice?'
}];